/**
 * Reports API endpoints
 */
import { api } from './baseApi';

export interface FinancialReportParams {
  date_from?: string;
  date_to?: string;
}

export interface OrdersReportParams {
  date_from?: string;
  date_to?: string;
  status?: string;
  service_id?: number;
}

export interface UsersReportParams {
  date_from?: string;
  date_to?: string;
  is_active?: boolean;
}

export interface ServicesReportParams {
  is_active?: boolean;
}

export interface ProvidersReportParams {
  date_from?: string;
  date_to?: string;
  provider_type?: 'SPECIALIST' | 'CONSULTANT';
  status?: string;
  is_active?: boolean;
}

export interface FinancialReportResponse {
  success: boolean;
  data: {
    overview: {
      total_payments: number;
      total_revenue: number;
      pending_amount: number;
      failed_amount: number;
      average_payment: number;
    };
    status_breakdown: Array<{
      status: string;
      count: number;
      total_amount: number;
    }>;
    method_breakdown: Array<{
      payment_method: string;
      count: number;
      total_amount: number;
      completed_count: number;
      completed_amount: number;
    }>;
    daily_revenue: Array<{
      date: string;
      revenue: number;
      count: number;
    }>;
    monthly_revenue: Array<{
      month: string;
      revenue: number;
      count: number;
    }>;
  };
}

export interface OrdersReportResponse {
  success: boolean;
  data: {
    overview: {
      total_orders: number;
      total_amount: number;
      average_order_value: number;
    };
    status_breakdown: Array<{
      status: string;
      count: number;
      total_amount: number;
    }>;
    service_breakdown: Array<{
      service_id: number;
      service_name: string;
      service_name_ar?: string;
      count: number;
      /** All orders, including open and cancelled ones */
      total_amount: number;
      completed_count?: number;
      /** Completed orders only (money earned) */
      completed_amount?: number;
    }>;
    subservice_breakdown: Array<{
      subservice_id: number;
      subservice_name: string;
      service_name: string;
      count: number;
      total_amount: number;
    }>;
    daily_orders: Array<{
      date: string;
      count: number;
      total_amount: number;
    }>;
  };
}

export interface UsersReportResponse {
  success: boolean;
  data: {
    users_overview: {
      total_users: number;
      active_users: number;
      verified_users: number;
      inactive_users: number;
    };
    patients_overview: {
      total_patients: number;
      active_patients: number;
      inactive_patients: number;
      patients_with_insurance: number;
    };
    gender_breakdown: Array<{
      gender: string;
      count: number;
    }>;
    daily_users: Array<{
      date: string;
      count: number;
    }>;
    daily_patients: Array<{
      date: string;
      count: number;
    }>;
  };
}

export interface ServicesReportResponse {
  success: boolean;
  data: {
    overview: {
      total_services: number;
      active_services: number;
      inactive_services: number;
      total_subservices: number;
      active_subservices: number;
      inactive_subservices: number;
    };
    top_services: Array<{
      service_id: number;
      service_name: string;
      order_count: number;
      total_revenue: number;
    }>;
    top_subservices: Array<{
      subservice_id: number;
      subservice_name: string;
      service_name: string;
      order_count: number;
      total_revenue: number;
    }>;
  };
}

export interface ProvidersReportResponse {
  success: boolean;
  data: {
    specialists_overview: {
      total_specialists: number;
      active_specialists: number;
      approved_specialists: number;
    };
    consultants_overview: {
      total_consultants: number;
      active_consultants: number;
      approved_consultants: number;
    };
    specialist_status_breakdown: Array<{
      status: string;
      count: number;
    }>;
    consultant_status_breakdown: Array<{
      status: string;
      count: number;
    }>;
    top_specialists: Array<{
      id: number;
      full_name: string;
      email: string;
      rating: string;
      total_reviews: number;
      total_orders: number;
    }>;
    top_consultants: Array<{
      id: number;
      full_name: string;
      email: string;
      rating: string;
      total_reviews: number;
      total_consultations: number;
    }>;
  };
}

export const reportsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getFinancialReport: builder.query<FinancialReportResponse, FinancialReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/financial/',
        params,
      }),
      providesTags: ['Statistics'],
    }),
    getOrdersReport: builder.query<OrdersReportResponse, OrdersReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/orders/',
        params,
      }),
      providesTags: ['Statistics'],
    }),
    getUsersReport: builder.query<UsersReportResponse, UsersReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/users/',
        params,
      }),
      providesTags: ['Statistics'],
    }),
    getServicesReport: builder.query<ServicesReportResponse, ServicesReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/services/',
        params,
      }),
      providesTags: ['Statistics'],
    }),
    getProvidersReport: builder.query<ProvidersReportResponse, ProvidersReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/providers/',
        params,
      }),
      providesTags: ['Statistics'],
    }),
    exportFinancialReport: builder.query<Blob, FinancialReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/financial/export/',
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),
    exportOrdersReport: builder.query<Blob, OrdersReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/orders/export/',
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),
    exportUsersReport: builder.query<Blob, UsersReportParams>({
      query: (params) => ({
        url: '/dashboard/reports/users/export/',
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetFinancialReportQuery,
  useGetOrdersReportQuery,
  useGetUsersReportQuery,
  useGetServicesReportQuery,
  useGetProvidersReportQuery,
  useLazyExportFinancialReportQuery,
  useLazyExportOrdersReportQuery,
  useLazyExportUsersReportQuery,
} = reportsApi;

